import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { List, Map as MapIcon, SlidersHorizontal, Navigation, X, ShieldCheck, Bookmark } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { CAUSES, DISTRICTS, CREDENTIAL_TYPES, BUDGET_BANDS } from "@/lib/causes";
import { NGOCard } from "@/components/NGOCard";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export default function Discover() {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [showFilters, setShowFilters] = useState(false);
  const [geo, setGeo] = useState(null);

  const f = {
    search: params.get("search") || "",
    cause: params.get("cause") || "",
    district: params.get("district") || "",
    tier: params.get("tier") || "",
    credential: params.get("credential") || "",
    budget: params.get("budget") || "",
    csr_eligible: params.get("csr_eligible") === "true",
    volunteer: params.get("volunteer") === "true",
  };

  const setF = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v === false) next.delete(k); else next.set(k, v);
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const qp = new URLSearchParams();
    ["search", "cause", "district", "tier", "credential", "budget"].forEach((k) => { if (f[k]) qp.set(k, f[k]); });
    if (f.csr_eligible) qp.set("csr_eligible", "true");
    if (f.volunteer) qp.set("volunteer", "true");
    const nlat = params.get("near_lat"), nlng = params.get("near_lng"), rad = params.get("radius");
    if (nlat && nlng) { qp.set("near_lat", nlat); qp.set("near_lng", nlng); qp.set("radius_km", rad || "50"); }
    try {
      const { data } = await api.get(`/orgs?${qp.toString()}`);
      setOrgs(data.orgs || []);
    } catch (e) { setOrgs([]); } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const nearMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const next = new URLSearchParams(params);
      next.set("near_lat", pos.coords.latitude); next.set("near_lng", pos.coords.longitude); next.set("radius", "50");
      setParams(next);
      setGeo([pos.coords.latitude, pos.coords.longitude]);
    });
  };

  const clearAll = () => setParams(new URLSearchParams());

  const saveSearch = async () => {
    const name = window.prompt("Name this saved search", f.cause || f.district || "My search");
    if (!name) return;
    try {
      await api.post("/saved-searches", { name, params: { cause: f.cause, district: f.district, budget: f.budget, csr_eligible: f.csr_eligible ? "true" : "" } });
      toast.success("Saved. We'll alert you when a new eligible partner appears in this filter.");
    } catch (e) { toast.error("Could not save search"); }
  };
  const activeCount = ["cause", "district", "tier", "credential", "budget"].filter((k) => f[k]).length + (f.csr_eligible ? 1 : 0) + (f.volunteer ? 1 : 0);

  const FilterPanel = () => (
    <div className="space-y-5">
      <Field label={t("filter.cause")}>
        <Select value={f.cause} onChange={(v) => setF("cause", v)} opts={[["", t("filter.any")], ...CAUSES.map((c) => [c.id, lang === "ta" ? c.name_ta : c.name])]} testid="search-cause-filter" />
      </Field>
      <Field label={t("filter.district")}>
        <Select value={f.district} onChange={(v) => setF("district", v)} opts={[["", t("filter.any")], ...DISTRICTS.map((d) => [d.id, d.name])]} testid="search-district-filter" />
      </Field>
      <Field label={t("filter.tier")}>
        <Select value={f.tier} onChange={(v) => setF("tier", v)} opts={[["", t("filter.any")], ["1", "Tier 1+"], ["2", "Tier 2+"], ["3", "Tier 3 only"]]} testid="search-tier-filter" />
      </Field>
      <Field label={t("filter.credential")}>
        <Select value={f.credential} onChange={(v) => setF("credential", v)} opts={[["", t("filter.any")], ...CREDENTIAL_TYPES.map((c) => [c.id, c.label])]} testid="search-credential-filter" />
      </Field>
      <Field label={t("filter.budget")}>
        <Select value={f.budget} onChange={(v) => setF("budget", v)} opts={[["", t("filter.any")], ...BUDGET_BANDS.map((b) => [b.id, b.label])]} testid="search-budget-filter" />
      </Field>
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="flex items-center gap-2 text-sm font-medium text-stone-700"><ShieldCheck size={16} className="text-tier2-icon" /> {t("discover.csr_only")}</span>
        <Switch checked={f.csr_eligible} onCheckedChange={(v) => setF("csr_eligible", v)} data-testid="csr-eligible-toggle" />
      </label>
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="text-sm font-medium text-stone-700">{t("discover.volunteer")}</span>
        <Switch checked={f.volunteer} onCheckedChange={(v) => setF("volunteer", v)} data-testid="volunteer-toggle" />
      </label>
      <Button variant="outline" size="sm" onClick={nearMe} className="w-full" data-testid="near-me-btn"><Navigation size={15} className="mr-1.5" /> {t("common.near_me")}</Button>
      {activeCount > 0 && <Button variant="ghost" size="sm" onClick={clearAll} className="w-full text-stone-500" data-testid="clear-filters-btn">{t("common.clear")}</Button>}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-brand">{t("discover.title")}</h1>
          <p className="text-sm text-stone-500 mt-1">{loading ? t("common.loading") : `${orgs.length} ${t("discover.results")}`}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-stone-300 overflow-hidden">
            <button onClick={() => setView("list")} data-testid="view-list-btn" className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${view === "list" ? "bg-slate-brand text-white" : "text-stone-600"}`}><List size={16} /> {t("common.list")}</button>
            <button onClick={() => setView("map")} data-testid="view-map-btn" className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${view === "map" ? "bg-slate-brand text-white" : "text-stone-600"}`}><MapIcon size={16} /> {t("common.map")}</button>
          </div>
          {(user?.role === "csr_manager" || user?.role === "admin") && (
            <Button variant="outline" size="sm" onClick={saveSearch} data-testid="save-search-btn"><Bookmark size={15} className="mr-1.5" /> Save search</Button>
          )}
          <Button variant="outline" size="sm" className="lg:hidden" onClick={() => setShowFilters(true)} data-testid="open-filters-btn">
            <SlidersHorizontal size={15} className="mr-1.5" /> {t("common.filters")}{activeCount > 0 && ` (${activeCount})`}
          </Button>
        </div>
      </div>

      <div className="flex gap-8">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24 bg-white rounded-xl border border-stone-200 p-5">
            <h2 className="font-semibold text-stone-900 mb-4 flex items-center gap-2"><SlidersHorizontal size={16} /> {t("common.filters")}</h2>
            <FilterPanel />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          {view === "map" ? (
            <div className="h-[600px] rounded-2xl overflow-hidden border border-stone-300">
              {!loading && <MapView orgs={orgs} center={geo || [11.2, 77.5]} />}
            </div>
          ) : loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="h-72 rounded-xl bg-stone-100 animate-pulse" />)}
            </div>
          ) : orgs.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-stone-200">
              <p className="text-stone-600">{t("discover.zero")}</p>
              <Button variant="outline" className="mt-4" onClick={clearAll}>{t("common.clear")}</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="ngo-results-grid">
              {orgs.map((o) => <NGOCard key={o.id} org={o} />)}
            </div>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-white p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-stone-900">{t("common.filters")}</h2>
              <button onClick={() => setShowFilters(false)} data-testid="close-filters-btn"><X size={20} /></button>
            </div>
            <FilterPanel />
            <Button className="w-full mt-5 bg-terracotta hover:bg-terracotta-hover" onClick={() => setShowFilters(false)}>Show {orgs.length} results</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, opts, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-terracotta">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
